import React from 'react';
import { Drawer, Typography, IconButton, Box, Select, MenuItem } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import Button from '@mui/material/Button';

// Custom chevron for select
const CustomChevron = (props) => (
    <svg {...props} style={{ right: 16, position: 'absolute', pointerEvents: 'none', color: '#666' }} width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const DarkInput = ({ label, value, onChange, placeholder, select, children, endAdornment, type = "text" }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" sx={{ color: '#aaa', mb: 0.5, display: 'block' }}>{label}</Typography>
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#111',
            border: '1px solid #333',
            borderRadius: '6px',
            overflow: 'hidden',
            '&:focus-within': { border: '1px solid #3c50b4' }
        }}>
            {select ? (
                <Select
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    IconComponent={CustomChevron}
                    sx={{
                        width: '100%',
                        color: '#fff',
                        '.MuiOutlinedInput-notchedOutline': { border: 0 },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
                        '.MuiSelect-select': { padding: '12px', backgroundColor: 'transparent' }
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                bgcolor: '#1e1e1e',
                                color: '#fff',
                                mt: 1,
                                borderRadius: '8px',
                                border: '1px solid #333',
                                '& .MuiMenuItem-root': {
                                    px: 2, py: 1.5,
                                    '&:hover': { bgcolor: '#333' },
                                    '&.Mui-selected': { bgcolor: '#4f5b93 !important' },
                                    '&.Mui-selected:hover': { bgcolor: '#3c50b4 !important' }
                                }
                            }
                        }
                    }}
                >
                    <MenuItem value="" disabled sx={{ color: '#888', display: value ? 'none' : 'block' }}>Select Task Type</MenuItem>
                    {children}
                </Select>
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    style={{
                        width: '100%', padding: '12px', backgroundColor: 'transparent', color: '#fff',
                        border: 'none', outline: 'none'
                    }}
                />
            )}
            {endAdornment && (
                <Box sx={{ pr: 2, pl: 1, color: '#aaa', fontSize: '0.85rem', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                    {endAdornment}
                </Box>
            )}
        </Box>
    </Box>
);

export default function EditAssignmentDialog({
    open,
    onClose,
    assignment,
    setAssignment,
    handleSave,
}) {
    if (!assignment) return null;

    const taskType = assignment.type || '';

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    backgroundColor: '#141414',
                    color: '#fff',
                    width: { xs: '100%', sm: '400px' },
                    p: 0,
                    top: '64px',
                    height: 'calc(100% - 64px)',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <div className="flex items-center p-4 pb-2 border-b border-gray-800">
                <IconButton onClick={onClose} sx={{ color: '#aaa', border: '1px solid #333', borderRadius: '8px', p: 1, mr: 2, '&:hover': { color: '#fff', backgroundColor: '#333' } }}>
                    <ArrowBackIosNewIcon sx={{ fontSize: '16px' }} />
                </IconButton>
                <Typography variant="subtitle1" fontWeight="bold">
                    Edit Assignment
                </Typography>
            </div>
            <Box sx={{ flex: 1, p: 4, pt: 3, pb: 2, overflowX: 'hidden', overflowY: 'auto' }}>
                <DarkInput
                    label="Task Name"
                    placeholder="e.g. Morning Cardio"
                    value={assignment.dailyTask?.title || ''}
                    readOnly
                />

                <DarkInput
                    label="User Name"
                    placeholder="e.g. Sarah Johnson"
                    value={assignment.customer?.name || ''}
                    readOnly
                />

                <DarkInput
                    label="Task Type"
                    value={assignment.dailyTask?.taskType || ''}
                    readOnly
                />

                {/* Dynamic Value Input */}
                <DarkInput
                    label="Target / Response Value"
                    placeholder="e.g. 45"
                    value={assignment.value || assignment.dailyTask?.targetValue || ''}
                    onChange={(e) => setAssignment({ ...assignment, value: e.target.value })}
                    endAdornment={
                        assignment.dailyTask?.taskType === 'workout' ? 'min' : 
                        ['food', 'breakfast', 'lunch', 'dinner', 'diet'].includes(assignment.dailyTask?.taskType) ? 'gm' :
                        ['water', 'beverage', 'beverages'].includes(assignment.dailyTask?.taskType) ? 'L' : ''
                    }
                />

                {assignment.dailyTask?.taskType === 'food' && (
                    <DarkInput
                        label="Calories (kcal)"
                        type="number"
                        placeholder="420"
                        value={assignment.dailyTask?.calories || ''}
                        readOnly
                        endAdornment="kcal"
                    />
                )}

                <DarkInput
                    label="Date"
                    type="date"
                    value={assignment.date ? assignment.date.split('T')[0] : ''}
                    onChange={(e) => setAssignment({ ...assignment, date: e.target.value })}
                />
            </Box>

            <Box sx={{ p: 4, pt: 2, pb: 4, borderTop: '1px solid #222', backgroundColor: '#141414' }}>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSave}
                    sx={{
                        backgroundColor: '#4f5b93',
                        color: '#fff',
                        textTransform: 'none',
                        py: 1.2,
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        '&:hover': { backgroundColor: '#3c50b4' }
                    }}
                >
                    Save changes
                </Button>
            </Box>
        </Drawer>
    );
}
